import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { articlesAPI } from '../services/api';
import { formatPublishTime, formatAuthorByline } from '../utils/dateUtils';
import SEO from '../components/SEO';
import RelativeTime from '../components/RelativeTime';
import './CategoryPages.css';

function FilmPage() {
  const location = useLocation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine category based on current path
  const getCategoryInfo = () => {
    const path = location.pathname;
    switch(path) {
      case '/film':
        return { name: 'Film', slug: 'film', title: 'Film News', subtitle: 'Latest movie news, reviews, and box office updates' };
      case '/awards':
        return { name: 'Awards', slug: 'awards', title: 'Awards Season', subtitle: 'Coverage of film and TV awards ceremonies' };
      case '/festivals':
        return { name: 'Festivals', slug: 'festivals', title: 'Film Festivals', subtitle: 'News from major film festivals worldwide' };
      case '/reviews':
        return { name: 'Reviews', slug: 'reviews', title: 'Reviews', subtitle: 'In-depth movie and TV show reviews' };
      default:
        return { name: 'Film', slug: 'film', title: 'Film News', subtitle: 'Latest movie news and updates' };
    }
  };

  const categoryInfo = getCategoryInfo();

  useEffect(() => {
    const fetchCategoryArticles = async () => {
      try {
        setLoading(true);
        // Get all articles and filter by category
        const response = await articlesAPI.getArticles(1, 50);
        const articles = response.posts || response || [];
        
        // AWS category name mapping (case insensitive) - try multiple variations
        const categoryArticles = articles.filter(article => {
          if (!article.category) return false;
          
          const articleCategory = article.category.toLowerCase().trim();
          const targetCategory = categoryInfo.slug.toLowerCase();
          
          // Try exact match first
          if (articleCategory === targetCategory) return true;
          
          // Try partial matches for common variations
          if (targetCategory === 'film' && (articleCategory === 'films' || articleCategory.includes('film'))) return true;
          if (targetCategory === 'tv' && (articleCategory === 'television' || articleCategory.includes('tv'))) return true;
          
          return false;
        });
        
        // Articles are already formatted from AWS
        const transformedArticles = categoryArticles;
        
        setArticles(transformedArticles);
        setError(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryArticles();
  }, [location.pathname, categoryInfo.slug]);

  if (loading) return <div className="loading">Loading {categoryInfo.name.toLowerCase()} articles...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  // Limit to 20 articles total per page
  const limitedArticles = articles.slice(0, 20);
  const featuredArticles = limitedArticles.slice(0, 1); // First article as featured
  const otherArticles = limitedArticles.slice(1); // Remaining articles (up to 19)

  return (
    <main className="category-page">
      <SEO 
        title={`${categoryInfo.title} - Entertainment Industry Updates`}
        description={`${categoryInfo.subtitle} - Stay updated with E Forum.`}
      />
      
      <div className="category-hero">
        <div className="category-header">
          <h1 className="category-title">{categoryInfo.title}</h1>
          <p className="category-subtitle">{categoryInfo.subtitle}</p>
        </div>
      </div>

      <div className="content-wrapper">
        {featuredArticles.length > 0 && (
          <section className="featured-section">
            <h2 className="section-title">Featured {categoryInfo.name} Story</h2>
            <Link to={`/article/${featuredArticles[0].id}`} className="featured-article">
              <img src={featuredArticles[0].image} alt={featuredArticles[0].title} />
              <div className="featured-content">
                <span className="category-badge">{featuredArticles[0].category}</span>
                <h3>{featuredArticles[0].title}</h3>
                <p>{featuredArticles[0].subtitle}</p>
                <div className="article-byline">
                  <div className="author-name">{featuredArticles[0].authorByline}</div>
                  <RelativeTime date={featuredArticles[0].createdAt || featuredArticles[0].publishTime || featuredArticles[0].fullDate || featuredArticles[0].date} className="publish-time" />
                </div>
              </div>
            </Link>
          </section>
        )}

        {otherArticles.length > 0 && (
          <section className="featured-section">
            <h2 className="section-title">{categoryInfo.name} Stories</h2>
            {otherArticles.map(article => (
              <Link key={article.id} to={`/article/${article.id}`} className="featured-article">
                <img src={article.image} alt={article.title} />
                <div className="featured-content">
                  <span className="category-badge">{article.category}</span>
                  <h3>{article.title}</h3>
                  <p>{article.subtitle}</p>
                  <div className="article-byline">
                    <div className="author-name">{article.authorByline}</div>
                    <RelativeTime date={article.createdAt || article.publishTime || article.fullDate || article.date} className="publish-time" />
                  </div>
                </div>
              </Link>
            ))}
          </section>
        )}
      </div>
    </main>
  );
}

export default FilmPage;